import { Loader2, MonitorCheck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useConferenciasInputFixa, useConferirInputFixaAgora } from '@/hooks/useConferenciasInputFixa';
import type { StatusConferenciaInputFixa } from '@/lib/inputFixaConferencia';

const SELO: Record<StatusConferenciaInputFixa, { texto: string; classe: string }> = {
  aprovado: { texto: 'Aprovado pela IA', classe: 'bg-success-soft text-success-text' },
  reprovado: { texto: 'Reprovado pela IA', classe: 'bg-destructive/10 text-destructive' },
  erro: { texto: 'IA não conferiu', classe: 'bg-destructive/10 text-destructive' },
  invalidada: { texto: 'Aprovação caiu', classe: 'bg-primary/10 text-primary' },
};

const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function ConferenciaInputFixaCard({ pedidoId, temPrints }: { pedidoId: string; temPrints: boolean }) {
  const { toast } = useToast();
  const { data: conferencias, isLoading } = useConferenciasInputFixa(pedidoId, true);
  const conferir = useConferirInputFixaAgora(pedidoId);

  if (isLoading) return null;
  const ultima = conferencias?.[0];
  if (!ultima && !temPrints) return null;

  const rodar = () => conferir.mutate(undefined, {
    onError: (e) => toast({ variant: 'destructive', title: 'A conferência da IA falhou', description: e instanceof Error ? e.message : String(e) }),
  });

  return (
    <div className="mb-4 rounded-lg border border-border bg-card py-3 pl-4 pr-3 border-l-[3px] border-l-primary space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <MonitorCheck className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Documentos de Input Fixa</span>
        {ultima && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SELO[ultima.status].classe}`}>
            {SELO[ultima.status].texto}
          </span>
        )}
        {ultima?.status !== 'aprovado' && (
          <Button variant="outline" size="sm" className="ml-auto h-[29px] text-xs" onClick={rodar} disabled={conferir.isPending}>
            {conferir.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
            Conferir de novo
          </Button>
        )}
      </div>
      {ultima ? (
        <>
          <p className="text-xs text-muted-foreground tabular-nums">
            {ultima.momento === 'encaminhamento' ? 'No envio' : 'No import'} · {quando(ultima.criado_em)}
            {ultima.usuario_nome ? ` · ${ultima.usuario_nome}` : ''}
            {ultima.contratos.length ? ` · contrato ${ultima.contratos.join(' / ')}` : ''}
          </p>
          {ultima.motivos.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs">
              {ultima.motivos.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
          {ultima.status === 'aprovado' && (
            <p className="text-xs text-muted-foreground">No envio para Input realizado Fixa o CRM confere de novo contra o contrato do pedido, sem reler os prints.</p>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">Os prints ainda não foram conferidos pela IA.</p>
      )}
    </div>
  );
}
